// D98.2 - Details tab: the printed-header fields, credit notes, dismissed-alert history.
// Props:
//   invoice / setInvoice(next)  = same shape as the old InvoiceHeaderBlock (patch = { ...invoice, [k]: v })
//   readOnly                    = cancelled (D96.18): every header field disabled
//   creditNotes                 = [{ _id, creditNoteNumber, type, grandTotal }]
//   grandTotal, creditTotal     = the page's summary.invoiceGrandTotal and its (negative) credit sum; net = grand + credit
//   currency                    = invoice currency code
//   onOpenCreditNote(id)        = navigate to /credit-notes/:id
// The alert history is read from invoice.alertHistory: dismissed entries only, read-only.
import React from 'react';
import { formatDateTime } from '../../utils/dateFormat';
import DateInput from '../../components/DateInput';
import { moneyOrBlank } from './profitabilityRows';
import { payerOf } from '../../lib/clientInvoiceApi';
import { CATEGORY_SELECT_OPTIONS } from '../../utils/invoiceCategories';

// D153.6 — who pays; the server refuses Mark sent and the tax-invoice PDF until the payer's name and address are in.
const PAYER_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'agent', label: 'Agent' },
];


type Props = {
  invoice: any;
  setInvoice: (next: any, typedField?: string) => void;
  readOnly: boolean;
  creditNotes: ReadonlyArray<{ _id: string; creditNoteNumber?: string; type?: string; grandTotal?: number }>;
  grandTotal: number | null | undefined;
  creditTotal: number;
  currency?: string | null;
  onOpenCreditNote: (id: string) => void;
  /** D109.6: false while the Invoice Date box holds a refused date; the page then refuses to save. */
  onInvoiceDateValidityChange: (valid: boolean) => void;
};

const inputClass = 'h-8 w-full rounded-md border border-slate-200 px-2 text-xs outline-none focus:border-primary-500 disabled:bg-slate-50 disabled:text-slate-500';
const labelClass = 'mb-1 block text-xs font-semibold text-slate-600';
const areaClass = 'min-h-[60px] w-full resize-y rounded-md border border-slate-200 p-2 text-xs outline-none focus:border-primary-500 disabled:bg-slate-50 disabled:text-slate-500';

export function DetailsTab({ invoice, setInvoice, readOnly, creditNotes, grandTotal, creditTotal, currency, onOpenCreditNote, onInvoiceDateValidityChange }: Props) {
  // A typed field passes its name as the 2nd argument so the page can fold one field's keystrokes into one undo
  // step (D119.11); the Category select is a pick, always its own step.
  const patch = (k: string, v: string) => setInvoice({ ...invoice, [k]: v }, k);
  const field = (k: string, label: string, placeholder?: string, type = 'text') => (
    <div>
      <label className={labelClass} htmlFor={`hdr-${k}`}>{label}</label>
      {type === 'date' ? (
        // D109.6's validity gate belongs to Invoice Date alone (the page refuses
        // Save on it, `refuseBlankInvoiceDate`): Due Date has no such gate, so a
        // refused typed date there is simply never emitted ("immediate" — Item 5).
        <DateInput id={`hdr-${k}`} className={inputClass} disabled={readOnly} value={invoice?.[k] || ''} onChange={(v) => patch(k, v)} validity={k === 'invoiceDate' ? onInvoiceDateValidityChange : 'immediate'} />
      ) : (
        <input id={`hdr-${k}`} type={type} className={inputClass} disabled={readOnly} value={invoice?.[k] || ''} onChange={(e) => patch(k, e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
  const address = (k: string, label: string, placeholder: string) => (
    <div className="md:col-span-2">
      <label className={labelClass} htmlFor={`hdr-${k}`}>{label}</label>
      <textarea id={`hdr-${k}`} className={areaClass} disabled={readOnly} value={invoice?.[k] || ''} onChange={(e) => patch(k, e.target.value)} placeholder={placeholder} rows={3} />
    </div>
  );
  const dismissed = (Array.isArray(invoice?.alertHistory) ? invoice.alertHistory : []).filter((h: any) => h?.dismissedAt);
  const m = (v: number | null | undefined) => moneyOrBlank(v, currency);

  return (
    <div className="space-y-5">
      <section className="rounded-md border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-800">Invoice header (appears on every printed page)</div>
          <div className="text-xs text-slate-500">{readOnly ? 'Cancelled - read only' : 'All fields editable'}</div>
        </div>
        <p className="mb-3 text-xs text-slate-500">A blank field prints blank on the invoice. The payer&apos;s name and address must be filled in before it can be sent or printed.</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          {field('vesselName', 'Vessel Name', 'MV Atlantic')}
          {field('ownerName', 'Owner Name', 'Vessel owner')}
          {field('agentName', 'Agent Name', 'Local/port agent')}
          <div>
            <label className={labelClass} htmlFor="hdr-payer">Payer</label>
            <select id="hdr-payer" className={inputClass} disabled={readOnly} value={payerOf(invoice)} onChange={(e) => setInvoice({ ...invoice, payer: e.target.value })}>
              {PAYER_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          {address('ownerAddress', 'Owner Address', 'Full owner address (up to 5 lines)')}
          {address('agentAddress', 'Agent Address', 'Full agent address (up to 5 lines)')}
          <div>
            <label className={labelClass} htmlFor="hdr-category">Category</label>
            <select id="hdr-category" className={inputClass} disabled={readOnly} value={invoice?.category || ''} onChange={(e) => setInvoice({ ...invoice, category: e.target.value })}>
              {CATEGORY_SELECT_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass} htmlFor="hdr-invoiceNumber">Invoice Number</label>
            <input id="hdr-invoiceNumber" className={`${inputClass} bg-slate-50 text-slate-500`} value={invoice?.invoiceNumber || ''} readOnly placeholder="Auto-generated" />
          </div>
          {field('clientPoNumber', 'Client PO Number', "Client's PO ref")}
          {field('invoiceDate', 'Invoice Date', undefined, 'date')}
          {/* Item 5 (9 Oct QA retest): due date and payment terms already exist on the
              model and are populated at generation (clientInvoice.service.js
              buildClientInvoiceSections / createManualClientInvoice) — this tab never
              rendered them. VAT is not added here: a vessel is never charged VAT
              (D53/D98.9), already stated on the Billing tab ("No VAT..."); a 0.00 VAT
              row here would read as if VAT math applied. */}
          {field('dueDate', 'Due Date', undefined, 'date')}
          {field('paymentTerms', 'Payment Terms', 'e.g. 30 days net')}
        </div>
      </section>

      {creditNotes.length > 0 && (
        <section className="rounded-md border border-purple-200 bg-purple-50/40">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-purple-100 px-4 py-2.5">
            <div className="text-sm font-semibold text-purple-800">Credit notes ({creditNotes.length})</div>
            <div className="text-xs text-slate-600">
              {m(grandTotal)} − {m(Math.abs(creditTotal))} = <span className="font-semibold text-slate-800">net {m((grandTotal || 0) + creditTotal)}</span>
            </div>
          </div>
          <div className="divide-y divide-purple-100">
            {creditNotes.map((cn) => (
              <button key={cn._id} type="button" onClick={() => onOpenCreditNote(cn._id)} className="flex w-full items-center justify-between px-4 py-2 text-left text-xs hover:bg-purple-50">
                <span className="font-medium text-slate-800">
                  {cn.creditNoteNumber}
                  <span className="ml-2 capitalize text-slate-500">{String(cn.type || '').replace('_', ' ')}</span>
                </span>
                <span className="font-semibold text-rose-700">{m(Number(cn.grandTotal || 0))}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-md border border-slate-200 bg-white p-4">
        <div className="mb-2 text-sm font-semibold text-slate-800">Dismissed alerts</div>
        {dismissed.length === 0 ? (
          <div className="text-xs text-slate-500">No alerts have been dismissed on this invoice.</div>
        ) : (
          <ul className="space-y-1.5">
            {dismissed.map((h: any) => (
              <li key={h.alertKey} className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                <div>{h.message}</div>
                <div className="text-slate-500">
                  Dismissed {formatDateTime(h.dismissedAt)} · {h.dismissedReason ? `Reason: ${h.dismissedReason}` : 'No reason given'}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
